import firebase from "firebase/compat";
import { compose, curry, forEachObjIndexed, isEmpty, isNil, mergeLeft } from "ramda";
import { GetListParams } from "react-admin";

import { TOTAL_PAGINATION } from "../constants";
import { store } from "../root/ReduxConfig";
import { DocumentSnapshot, OrderByDirection, Query, QuerySnapshot } from "../types/firebase.types";
import { PaginationDirection } from "../types/pagination.types";
import { IGetListParams } from "../types/reactAdmin.types";

const skipLists: string[] = [];
const defaultParams: IGetListParams = {
  pagination: {
    page: 1,
    perPage: 10,
  },
  sort: {
    field: "id",
    order: "desc",
  },
  filter: {},
  direction: PaginationDirection.current,
  record: {},
  limit: 10,
  additionalFilter: {},
  operators: {},
  additionalOperators: {},
  fieldOverrides: {},
  fieldValueOverrides: {},
  user: {},
};

const queryLimit = curry((params: IGetListParams, query: Query) => {
  const { limit } = params;
  return query.limit(limit);
});

const queryPagination = curry((params: IGetListParams, query: Query): Query => {
  const { direction, limit, record } = params;

  if (direction && record) {
    switch (direction) {
      case PaginationDirection.next:
        return query.startAfter(record.snapshot);
      case PaginationDirection.previous:
        return query.endBefore(record.snapshot).limitToLast(limit);
      case PaginationDirection.current:
        return query.startAt(record.snapshot);
      default:
        return query;
    }
  }
  return query;
});

const queryOrder = curry((params: IGetListParams, query: Query): Query => {
  const {
    sort: { field, order },
  } = params;
  if (!isNil(field) && field !== "id") {
    const orderBy = order.toLowerCase() as OrderByDirection;
    return query.orderBy(field, orderBy).orderBy(firebase.firestore.FieldPath.documentId(), orderBy);
  }
  return query;
});

const queryAutocomplete = curry((params: IGetListParams, query: Query): Query => {
  const { additionalFilter } = params;
  if (!isEmpty(additionalFilter)) {
    const { autoComplete } = additionalFilter;
    if (autoComplete)
      return query.orderBy(additionalFilter[autoComplete]).startAt(autoComplete).endAt(`${autoComplete}\uf8ff`);
  }
  return query;
});

const applyQueryFilters = curry((params: IGetListParams, query: Query): Query => {
  const { filter, additionalFilter, operators, additionalOperators, fieldOverrides, fieldValueOverrides } = params;
  let filteredQuery = query;
  const mergedFilters = mergeLeft(additionalFilter)(filter);
  if (!isNil(mergedFilters)) {
    forEachObjIndexed((value, key) => {
      if (key !== "autoComplete" && filter.autoComplete !== key) {
        filteredQuery = query = query.where(
          (fieldOverrides && fieldOverrides[key]) || key,
          (operators && operators[key]) || "==",
          (fieldValueOverrides && fieldValueOverrides[key]) || value
        );
      }
    })(filter);
    forEachObjIndexed((value, key) => {
      if (key !== "autoComplete" && additionalFilter.autoComplete !== key) {
        filteredQuery = query = query.where(
          (fieldOverrides && fieldOverrides[key]) || key,
          (additionalOperators && additionalOperators[key]) || "==",
          (fieldValueOverrides && fieldValueOverrides[key]) || value
        );
      }
    })(additionalFilter);
  }
  return filteredQuery;
});

const prepareParamsToPassFirestoreLimitations = curry((params: IGetListParams, query: Query): Query => {
  let {
    sort: { field },
    filter,
    operators,
  } = params;
  /**
   * firestore limitation
   * can't order with same field used in where clause with `==` operator
   * so we check if filter contains that filed and operators for that field is `==` then we change it to
   * default value like `createdAt`
   * */
  if (filter[field] && operators[field] === undefined) params.sort.field = "createdAt";

  return query;
});

export const executeListQuery = async (params: IGetListParams, query: Query): Promise<QuerySnapshot> => {
  const applyQueryFiltersWithData = applyQueryFilters(params);
  const queryAutocompleteWithData = queryAutocomplete(params);
  const queryOrderWithData = queryOrder(params);
  const queryPaginationWithData = queryPagination(params);
  const queryLimitWithData = queryLimit(params);
  const queryPrepareParamsToPassFirestoreLimitations = prepareParamsToPassFirestoreLimitations(params);

  // will execute the functions from right to left one by one
  const composedQuery = compose(
    queryPaginationWithData,
    queryLimitWithData,
    queryOrderWithData,
    queryAutocompleteWithData,
    applyQueryFiltersWithData,
    queryPrepareParamsToPassFirestoreLimitations
  );

  return composedQuery(query).get();
};

export const getPaginationData = (data: any, resource: string, page: number, perPage: number, skip: boolean) => {
  if (!data || !resource || !page || page === 1) return {};

  const resourceData = data.admin.resources[resource];
  const direction = data.pagination[resource] && data.pagination[resource].direction;
  const displayedIds = resourceData.list.ids;
  const cachedData = resourceData.data;

  let record;
  switch (direction) {
    case PaginationDirection.next: {
      const lastRecord = cachedData[displayedIds[displayedIds.length - 1]];
      record = lastRecord && lastRecord;
      break;
    }
    case PaginationDirection.previous: {
      const firstRecord = cachedData[displayedIds[0]];
      record = firstRecord && firstRecord;
      break;
    }
    default:
      /*
       * Note
       * we used the difference between displayed length and the 'perPage' count here
       * to solve the case of adding new record when being in the last page
       * because adding a new record inject the new created ID to the current displayed IDs
       *
       * also 'skip' argument is just a mark to go to the first page after creating a new record
       * */
      const diff = displayedIds.length - perPage;
      const firstRecord = cachedData[displayedIds[diff === 0 && !skip ? diff : -1]];
      record = firstRecord && firstRecord;
      break;
  }

  return {
    record,
    direction,
  };
};

export const prepareGetListParams = (resource: string, params: GetListParams): IGetListParams => {
  const data = store.getState();
  const { record, direction } = getPaginationData(
    data,
    resource,
    params.pagination.page,
    params.pagination.perPage,
    skipLists.includes(resource)
  );

  if (!record && params.pagination.page > 1) return { ...defaultParams, ...params };

  return {
    ...defaultParams,
    ...params,
    direction,
    record,
    user: data.user,
  };
};

export const getFirebaseCollectionData = (
  firebaseCollection: QuerySnapshot,
  formatFn: any = undefined
): Record<string, any>[] => {
  return firebaseCollection.docs.map((snapshot: DocumentSnapshot) => {
    const proto = { snapshot };
    const res = {
      ...snapshot.data(),
      id: snapshot.id,
      ref: snapshot.ref,
      snapshot,
      ...(formatFn ? formatFn(snapshot, snapshot.data()) : {}),
    };
    return Object.setPrototypeOf(res, proto);
  });
};

export const getFinalList = async (params: IGetListParams, query: Query) => {
  try {
    const querySnapshot = await executeListQuery(params, query);
    const formattedProperties = getFirebaseCollectionData(querySnapshot);
    return {
      data: formattedProperties,
      total: TOTAL_PAGINATION,
    };
  } catch (ex: any) {
    console.log(ex.message);
    return {
      data: [],
      total: TOTAL_PAGINATION,
    };
  } finally {
    console.log(params, query);
  }
};
